import { useMemo, useState } from "react";
import { ApiError } from "../../api/client";
import {
  addTrainingGroup,
  addTrainingSubgroup,
  deleteTrainingGroup,
  deleteTrainingSubgroup,
  useTrainingCohortTree,
} from "../../api/trainingCohorts";
import "./TrainingCohortManageModal.css";

type Props = {
  onClose: () => void;
  onChanged: () => void;
};

export function TrainingCohortManageModal({ onClose, onChanged }: Props) {
  const { tree, loading, error, reload } = useTrainingCohortTree();
  const [periodId, setPeriodId] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const period = useMemo(
    () => tree.find((p) => p.id === periodId),
    [tree, periodId],
  );

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      reload();
      onChanged();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Islem basarisiz.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-modal kp-card kp-cohort-manage">
        <div className="kp-modal__head">
          <div>
            <strong>Donem / Grup Yonetimi</strong>
            <span>Grup ve alt grup ekleme veya silme</span>
          </div>
          <button type="button" className="kp-btn kp-btn--ghost" onClick={onClose} disabled={busy}>
            Kapat
          </button>
        </div>

        <div className="kp-cohort-manage__body">
          <label className="kp-cohort-manage__field">
            <span>Donem secin</span>
            <select
              className="kp-input"
              value={periodId}
              disabled={loading || busy}
              onChange={(e) => setPeriodId(e.target.value)}
            >
              <option value="">—</option>
              {tree.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          {(error || actionError) && (
            <div className="kp-cohort-manage__error">{error || actionError}</div>
          )}

          {period && (
            <>
              <div className="kp-cohort-manage__actions">
                <button
                  type="button"
                  className="kp-btn"
                  disabled={busy}
                  onClick={() => run(async () => addTrainingGroup(period.id))}
                >
                  + Grup ekle
                </button>
              </div>

              <div className="kp-cohort-manage__tree">
                {period.groups.map((g) => (
                  <div key={g.id} className="kp-cohort-manage__group">
                    <div className="kp-cohort-manage__group-head">
                      <strong>{g.label}</strong>
                      <div className="kp-cohort-manage__group-btns">
                        <button
                          type="button"
                          className="kp-btn kp-btn--ghost"
                          disabled={busy}
                          onClick={() => run(async () => addTrainingSubgroup(g.id))}
                        >
                          + Alt grup
                        </button>
                        <button
                          type="button"
                          className="kp-btn kp-btn--ghost kp-cohort-manage__danger"
                          disabled={busy}
                          onClick={() => run(async () => deleteTrainingGroup(g.id))}
                        >
                          Grubu sil
                        </button>
                      </div>
                    </div>
                    <ul>
                      {g.subgroups.map((sg) => (
                        <li key={sg.id}>
                          <span>
                            {sg.label}
                            <small> ({sg.member_count} kursiyer)</small>
                          </span>
                          <button
                            type="button"
                            className="kp-btn kp-btn--ghost kp-cohort-manage__danger"
                            disabled={busy || sg.member_count > 0}
                            title={
                              sg.member_count > 0
                                ? "Kursiyer varken silinemez"
                                : "Alt grubu sil"
                            }
                            onClick={() => run(async () => deleteTrainingSubgroup(sg.id))}
                          >
                            Sil
                          </button>
                        </li>
                      ))}
                      {g.subgroups.length === 0 && (
                        <li className="kp-cohort-manage__empty">Alt grup yok</li>
                      )}
                    </ul>
                  </div>
                ))}
                {period.groups.length === 0 && (
                  <p className="kp-cohort-manage__empty">Bu donemde grup yok. Grup ekleyin.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
